/**
 * Which navigation app a driver's phone should open for turn-by-turn directions.
 *
 * A web app cannot see which apps are installed, so on iPhone the driver picks
 * once and the choice is remembered on that phone. Android phones essentially
 * always have Google Maps, and desktop gets Google Maps in a browser tab, so
 * neither ever sees the picker.
 */

const STORAGE_KEY = "preferredMapsApp";

export const MAPS_APPS = {
  apple: { label: "Apple Maps" },
  google: { label: "Google Maps" },
  waze: { label: "Waze" },
};

export const MAPS_APP_ORDER = ["apple", "google", "waze"];

export const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent || "")) return true;
  // iPadOS 13+ reports itself as a Mac but has a touch screen.
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
};

export const getPreferredMapsApp = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return MAPS_APPS[saved] ? saved : null;
  } catch (e) {
    return null;
  }
};

export const setPreferredMapsApp = (app) => {
  try {
    localStorage.setItem(STORAGE_KEY, app);
  } catch (e) {
    // Private browsing or blocked storage: the pick still works for this press.
  }
};

/**
 * Builds the hand-off link for one app.
 * destination: { address } or { lat, lng }.
 *
 * Google Maps and Waze start guidance from the phone's current location as soon
 * as they open. Apple Maps shows the route and waits for a "Go" tap; its URL
 * scheme has no auto-start. Each https link opens the native app when it is
 * installed and that app's website otherwise.
 */
export const buildMapsAppUrl = (app, destination) => {
  if (!destination) return null;
  const hasCoords =
    Number.isFinite(destination.lat) && Number.isFinite(destination.lng);
  const address =
    typeof destination.address === "string" ? destination.address.trim() : "";
  if (!address && !hasCoords) return null;

  const place = address || `${destination.lat},${destination.lng}`;
  const encodedPlace = encodeURIComponent(place);

  switch (app) {
    case "apple":
      return `https://maps.apple.com/?daddr=${encodedPlace}&dirflg=d`;
    case "waze":
      return address
        ? `https://waze.com/ul?q=${encodedPlace}&navigate=yes`
        : `https://waze.com/ul?ll=${destination.lat},${destination.lng}&navigate=yes`;
    case "google":
    default:
      return (
        "https://www.google.com/maps/dir/?api=1" +
        `&destination=${encodedPlace}` +
        "&travelmode=driving&dir_action=navigate"
      );
  }
};
